import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/mutations"

import {
  ConfirmTransferWorkOrderDocument,
  type ConfirmTransferWorkOrderFailure,
  type ConfirmTransferWorkOrderMutation,
  type ConfirmTransferWorkOrderMutationVariables,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseConfirmTransferWorkOrderReturn {
  confirmTransferWorkOrder: (transferId: string) => Promise<boolean>
  isConfirming: boolean
}

export const useConfirmTransferWorkOrder = (): UseConfirmTransferWorkOrderReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    ConfirmTransferWorkOrderMutation,
    ConfirmTransferWorkOrderMutationVariables
  >(ConfirmTransferWorkOrderDocument)

  const confirmTransferWorkOrder = useCallback(
    async (transferId: string): Promise<boolean> => {
      const { data, error } = await executeMutation({ input: { transferId } })
      const output = data?.transfer.confirmTransferWorkOrder
      const hasError = error || output?.__typename === "ConfirmTransferWorkOrderFailure"

      if (hasError) {
        const message = error?.message ?? (output as ConfirmTransferWorkOrderFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Work order confirmed" })
      return true
    },
    [executeMutation, notify]
  )

  return { confirmTransferWorkOrder, isConfirming: fetching }
}
